import React, { useState, useEffect } from 'react';

import { MOBILE_SLIDE_UP } from '@core/constants/ModalTypes';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { jobAlert } from 'services/pharmaJobs';
import { getErrorText } from 'utils/geterrortext';
import { useRouter } from 'next/router';
import { isUserLoggedIn } from 'utils/authentication';

import UseModal from 'hooks/use-modal';
import Modal from '@core/components/Modal/Modal';
import ActionButton from '@core/components/actionbutton/ActionButton';
import TextInput from '@core/components/textinput/TextInput';

import { Edit2 } from 'iconsax-react';
import BellIcon from 'assets/icons/solar-bell-linear.svg';

import classes from './JobAlert.module.scss';
import IconButton from '@core/components/iconbutton/IconButton';



const JobAlert = ({ toast }) => {
    const router = useRouter();
    const isAuthenticated = isUserLoggedIn();
    const [isEditingPreferences, setIsEditingPreferences] = useState(false);
    const [hasDefaultValues, setHasDefaultValues] = useState(false);
    const { show, showModal, destroyModal } = UseModal();
    const { show: showPreferences, showModal: showPreferencesModal, destroyModal: destroyPreferencesModal } = UseModal();
    const { data: jobAlertData, isLoading, refetch } = useQuery('jobAlert', jobAlert.GET);
    const { mutate: updateJobAlert } = useMutation(jobAlert.PUT, {
        onSuccess: () => {
            toast(
                'Job alert updated successfully',
                'success'
            );
            destroyModal();
            destroyPreferencesModal();
            refetch();
        },
        onError: (err) => {
            toast(
                getErrorText(err),
                'alert'
            );
        }
    });
    const { mutate: deleteJobAlert } = useMutation(jobAlert.DELETE, {
        onSuccess: () => {
            toast(
                'Job alert deleted successfully',
                'success'
            );
            destroyPreferencesModal();
            refetch();
        },
        onError: (err) => {
            toast(
                getErrorText(err),
                'alert'
            );
        }
    });
    const {
        register,
        handleSubmit,
        formState: { errors, isDirty, isValid },
        reset,
        setValue
    } = useForm({ mode: 'onChange' });

    useEffect(() => {
        setValue('keyword', router.query.search_keyword || '');
        setValue('location', router.query.location || '');
        setHasDefaultValues(!!(router.query.search_keyword || router.query.location));
    }, [router.query.search_keyword, router.query.location]);

    const openPreferences = () => {
        reset({
            keyword: jobAlertData?.data?.keyword || '',
            location: jobAlertData?.data?.location || ''
        });
        setIsEditingPreferences(false);
        showPreferencesModal();
    };

    const onSubmitAlert = (alertData) => {
        updateJobAlert(alertData);
    }

    const handleStartUpdate = () => {
        reset({
            keyword: jobAlertData?.data?.keyword || '',
            location: jobAlertData?.data?.location || ''
        });
        setIsEditingPreferences(true);
    }

    const handleOpenCreateModal = () => {
        if (!isAuthenticated) 
            router.push('/login?next=/jobs');        
        else
            showModal();

    };

    return (
        <>
            <div className={classes['wrapper']}>
                {!isLoading && !(jobAlertData?.data?.keyword || jobAlertData?.data?.location) && hasDefaultValues && (
                    <div className={`d-none d-md-block ${classes['job-alert-info']}`}>
                        Get weekly updates for this search. Create a job alert in one click.
                    </div>
                )}

                {!isLoading && (jobAlertData?.data?.keyword || jobAlertData?.data?.location) && (
                    <ActionButton kind='blank' onClick={openPreferences}>
                        <BellIcon />
                        Job Alert Preferences
                    </ActionButton>
                )}
                {!isLoading && !(jobAlertData?.data?.keyword || jobAlertData?.data?.location) && (
                    <ActionButton kind='blank' onClick={handleOpenCreateModal}>
                        <BellIcon />
                        Create Job Alert
                    </ActionButton>
                )}
            </div>
            <Modal
                show={show}
                closeHandler={destroyModal}
                type={MOBILE_SLIDE_UP}>
                <div className={classes['job-alert-modal']}>
                    <h3>Create Job Alert</h3>
                    <p>Choose one position and one region. Every week, we’ll send you how many new roles were posted and a 1-click link to view them.</p>
                    <form onSubmit={handleSubmit(onSubmitAlert)}>
                        <div className='px-5 mb-5'>
                            <TextInput
                                validation={{
                                    required: true
                                }}
                                input={{
                                    type: 'text',
                                    label: 'Job Position',
                                    name: 'keyword',
                                    placeholder: 'Job Position',
                                    id: 'keyword'
                                }}
                                errors={errors}
                                register={register}
                            />
                            <TextInput
                                input={{
                                    type: 'text',
                                    label: 'Location',
                                    name: 'location',
                                    placeholder: 'Location',
                                    id: 'location'
                                }}
                                errors={errors}
                                register={register}
                            />
                        </div>
                        <ActionButton onClick={handleSubmit(onSubmitAlert)} >
                            Save Alert
                        </ActionButton>
                    </form>
                </div>
            </Modal>
            <Modal
                show={showPreferences}
                closeHandler={destroyPreferencesModal}
                type={MOBILE_SLIDE_UP}>
                <div className={classes['job-alert-preferences-modal']}>
                    <h3>Job Alert Preferences</h3>
                    <p>Every week, we’ll send you how many new roles were posted and a 1-click link to view them.</p>
                    {!isEditingPreferences && (
                        <>
                            <div className={classes['preferences']}>
                                <div className='d-flex justify-content-between align-items-center mt-5  gap-2'>
                                    <p className='m-0'>Job Position:<strong> {jobAlertData?.data?.keyword || 'N/A'}</strong></p>
                                    <IconButton onClick={handleStartUpdate}>
                                        <Edit2 variant='TwoTone' color='#BB3A90' />
                                    </IconButton>
                                </div>
                                <div className='d-flex justify-content-between align-items-center mt-4 gap-2'>
                                    <p className='m-0'>Location:<strong> {jobAlertData?.data?.location || ''}</strong></p>
                                    <IconButton onClick={handleStartUpdate}>
                                        <Edit2 variant='TwoTone' color='#BB3A90' />
                                    </IconButton>
                                </div>
                            </div>
                            <div className={`d-flex mt-5 gap-2 ${classes['button-wrapper']}`}>
                                <ActionButton kind='simple' onClick={deleteJobAlert}>
                                    Delete Job Alert
                                </ActionButton>

                                <ActionButton onClick={destroyPreferencesModal}>
                                    Confirm
                                </ActionButton>
                            </div>
                        </>
                    )}
                    {isEditingPreferences && (
                        <form onSubmit={handleSubmit(onSubmitAlert)}>
                            <div className='px-4'>
                                <TextInput
                                    validation={{ required: true }}
                                    input={{ type: 'text', label: 'Job Position', name: 'keyword', placeholder: 'Job Position', id: 'pref-keyword' }}
                                    errors={errors}
                                    register={register}
                                />
                                <TextInput
                                    input={{ type: 'text', label: 'Location', name: 'location', placeholder: 'Location', id: 'pref-location' }}
                                    errors={errors}
                                    register={register}
                                />
                            </div>
                            <div className={`d-flex mt-4 gap-2 ${classes['button-wrapper']}`}>
                                <ActionButton kind='simple' onClick={deleteJobAlert}>
                                    Delete Job Alert
                                </ActionButton>
                                <ActionButton onClick={handleSubmit(onSubmitAlert)} disabled={!isDirty || !isValid}>
                                    Confirm
                                </ActionButton>
                            </div>
                        </form>
                    )}

                </div>
            </Modal>
        </>
    );
};

export default JobAlert;